/**
 * RFP Agent Plugin
 * 
 * Universal AI-powered tender discovery and proposal generation system.
 * 
 * Provides:
 * - Multi-source opportunity discovery (Canada Grants, SAM.gov, World Bank, etc.)
 * - Requirement extraction from RFP documents
 * - Past proposal search and evidence retrieval
 * - Draft generation with evidence citations
 * - Word count optimization
 * - Senior review workflow
 * - Compliance dashboard
 * - Awards tracking
 */

import { TraceKitPlugin } from '@tracekit/core';
import { 
  FileText, 
  CheckCircle, 
  Shield,
  ListChecks,
  Briefcase,
  Trophy,
  Database,
  Newspaper,
  Users,
  Building2,
  Award,
  Lightbulb,
  Target,
  TrendingUp,
  BarChart3,
} from 'lucide-react';

// Import page components
import { RequirementExtractionPage } from './pages/RequirementExtraction';
import { ReviewWorkflowPage } from './pages/ReviewWorkflow';
import { ComplianceDashboardPage } from './pages/ComplianceDashboard';
import { ProposalListPage } from './pages/ProposalList';
import { ProposalSearchPage } from './pages/ProposalSearch';
import { OpportunitiesDashboard } from './pages/OpportunitiesDashboard';
import { AwardsDashboard } from './pages/AwardsDashboard';
import { IntegrationsDashboard } from './pages/IntegrationsDashboard';
import { IndustryNewsPage } from './pages/IndustryNewsPage';
import { StaffManagementPage } from './pages/StaffManagementPage';
import { WorkPlanPage } from './pages/WorkPlanPage';
import { ProjectPortfolioPage } from './pages/ProjectPortfolioPage';
import { TechnicalApproachesPage } from './pages/TechnicalApproachesPage';
import { RequirementsExtractorPage } from './pages/RequirementsExtractorPage';
import { OpportunityMatchingPage } from './pages/OpportunityMatchingPage';
import { ExecutiveDashboard } from './pages/ExecutiveDashboard';
import { GovernmentDataDashboard } from './pages/GovernmentDataDashboard';
import { ProposalManagementDashboard } from './pages/ProposalManagementDashboard';
import { ProposalDetailPage } from './pages/ProposalDetailPage';
import { WorkPlanExecutionDashboard } from './pages/WorkPlanExecutionDashboard';
import { WorkplanSearchPage } from './pages/WorkplanSearchPage';
import { StageGateChecklist } from './pages/StageGateChecklist';

export const rfpAgentPlugin: TraceKitPlugin = {
  id: 'rfp-agent',
  name: 'RFP Agent',
  version: '1.0.0',
  description: 'Universal AI-powered tender discovery and proposal generation system',
  
  routes: [
    {
      path: '/rfp/external-data',
      component: GovernmentDataDashboard,
    },
    {
      path: '/rfp/proposals-management',
      component: ProposalManagementDashboard,
    },
    // Nested routes for proposal detail views (more specific paths first)
    {
      path: '/rfp/proposals-management/current',
      component: ProposalDetailPage,
    },
    {
      path: '/rfp/workplan-execution',
      component: WorkPlanExecutionDashboard,
    },
    // Backwards compatibility redirects - External Data
    {
      path: '/rfp/integrations',
      component: () => {
        window.location.href = '/rfp/external-data?tab=integrations';
        return null;
      },
    },
    {
      path: '/rfp/opportunities',
      component: () => {
        window.location.href = '/rfp/external-data?tab=opportunities';
        return null;
      },
    },
    {
      path: '/rfp/awards',
      component: () => {
        window.location.href = '/rfp/external-data?tab=awards';
        return null;
      },
    },
    {
      path: '/rfp/industry-news',
      component: () => {
        window.location.href = '/rfp/external-data?tab=news';
        return null;
      },
    },
    // Backwards compatibility redirects - Proposal Management
    {
      path: '/rfp/proposals',
      component: () => {
        window.location.href = '/rfp/proposals-management?tab=list';
        return null;
      },
    },
    {
      path: '/rfp/search',
      component: () => {
        window.location.href = '/rfp/proposals-management?tab=search';
        return null;
      },
    },
    {
      path: '/rfp/requirements-extractor',
      component: () => {
        window.location.href = '/rfp/proposals-management?tab=requirements';
        return null;
      },
    },
    {
      path: '/rfp/opportunity-matching',
      component: () => {
        window.location.href = '/rfp/proposals-management?tab=matching';
        return null;
      },
    },
    {
      path: '/rfp/review',
      component: () => {
        window.location.href = '/rfp/proposals-management?tab=review';
        return null;
      },
    },
    // Backwards compatibility redirects - Work Plan Execution
    {
      path: '/rfp/workplans',
      component: () => {
        window.location.href = '/rfp/workplan-execution?tab=workplan';
        return null;
      },
    },
    {
      path: '/rfp/technical-approaches',
      component: () => {
        window.location.href = '/rfp/workplan-execution?tab=technical';
        return null;
      },
    },
    {
      path: '/rfp/compliance',
      component: () => {
        window.location.href = '/rfp/workplan-execution?tab=compliance';
        return null;
      },
    },
    {
      path: '/rfp/portfolio',
      component: () => {
        window.location.href = '/rfp/workplan-execution?tab=portfolio';
        return null;
      },
    },
    // Supporting pages (not in consolidated dashboards)
    {
      path: '/rfp/extract',
      component: RequirementExtractionPage,
    },
    {
      path: '/rfp/staff-matching',
      component: StaffManagementPage,
    },
    {
      path: '/rfp/staff-data',
      component: StaffManagementPage,
    },
    {
      path: '/rfp/staff',
      component: StaffManagementPage,
    },
    {
      path: '/rfp/dashboard',
      component: ExecutiveDashboard,
    },
    {
      path: '/rfp/stage-gate-checklist',
      component: StageGateChecklist,
    },
  ],
  
  sidebarItems: [
    // Reordered per user request: AI Search, Exec Dash, Proposal Mgmt, Govt Data, Workplan, Data Mgmt, Staff Mgmt
    {
      label: 'Executive Dashboard',
      icon: BarChart3,
      path: '/rfp/dashboard',
      section: 'RFP Agent',
      description: 'Overview of all RFP activities and metrics',
      order: 1,
    },
    {
      label: 'Government Data',
      icon: Shield,
      path: '/rfp/external-data',
      section: 'RFP Agent',
      description: 'Integrations, Opportunities, Awards, News',
      order: 2,
    },
    {
      label: 'Proposal Management',
      icon: FileText,
      path: '/rfp/proposals-management',
      section: 'RFP Agent',
      description: 'List, Search, Requirements, Matching, Pipeline, Review',
      order: 3,
    },
    {
      label: 'Work Plan & Execution',
      icon: ListChecks,
      path: '/rfp/workplan-execution',
      section: 'RFP Agent',
      description: 'Work Plans, Technical Approaches, Compliance, Portfolio',
      order: 4,
    },
    {
      label: 'Data Pipeline',
      icon: Building2,
      path: '/data',
      section: 'RFP Agent',
      description: 'Sources, Artifacts, and Data Configuration',
      order: 5,
    },
    {
      label: 'Staff Management',
      icon: Users,
      path: '/rfp/staff-data',
      section: 'RFP Agent',
      description: 'Team qualifications and matching',
      order: 6,
    },
  ],
  
  onInit: async (context) => {
    console.log('RFP Agent plugin initialized', context);
    
    // Entity type registration removed (QA Framework cleanup - entity types are defined in policy pack)
    
    // Register help content for Help Center
    try {
      const { rfpAgentHelpContent } = await import('./help/rfpAgentHelpContent');
      // Import registry from core package exports
      const { helpContentRegistry } = await import('@tracekit/core');
      
      if (rfpAgentHelpContent && helpContentRegistry) {
        helpContentRegistry.register('rfp-agent', rfpAgentHelpContent);
        console.log(`[RFP Agent] Registered ${rfpAgentHelpContent.length} help articles in Help Center`);
      } else {
        console.warn('[RFP Agent] helpContentRegistry not found in core package');
      }
    } catch (e) {
      console.warn('[RFP Agent] Failed to register help content:', e);
    }
  },
  
  onProjectChange: async (projectId) => {
    console.log('RFP Agent: Project changed to', projectId);
  },
};

export default rfpAgentPlugin;

// Re-export key types and APIs for external consumption
export type { Opportunity, DataSourceConfig, IntegrationStatus } from './services/dataSourceIntegrations';
export type { OpportunityEntity } from './api/opportunities';
export type {
  DataSourceType,
  DataSourceMetadata,
  DataSourceRunner,
  ValidationResult,
  HealthCheckResult,
  DataSourceRegistryEntry,
} from './services/dataSourceRegistry';
export {
  fetchCanadaGrants,
  fetchCanadaTenders,
  fetchSAMGov,
  fetchWorldBank,
  uploadOpportunityDocument,
  getIntegrationStatus,
} from './services/dataSourceIntegrations';
export {
  queryOpportunities,
  queryAwards,
  saveOpportunity,
  createOpportunityEntity,
  markOpportunityAsAwarded,
} from './api/opportunities';
export {
  dataSourceRegistry,
  getAllDataSources,
  getDataSource,
  validateDataSource,
  testDataSource,
  fetchFromDataSource,
} from './services/dataSourceRegistry';

// Export components and types for tenant-specific configurations
export { IntegrationsDashboard } from './pages/IntegrationsDashboard';
export type { IntegrationsDashboardProps } from './pages/IntegrationsDashboard';
export { GovernmentDataDashboard } from './pages/GovernmentDataDashboard';
export { GeographicFilter } from './components/GeographicFilter';
export { ServiceLineFilter } from './components/ServiceLineFilter';
export type { GeographicConfig, GeographicFilterProps } from './components/GeographicFilter';
export type { ServiceLine, ServiceLineFilterProps } from './components/ServiceLineFilter';

// Export help content for Help Center
export { rfpAgentHelpContent } from './help/rfpAgentHelpContent';

// Entity type registration removed (QA Framework cleanup - entity types are defined in policy pack)

// Export Word Count components for ReportViewer integration
export { WordCountTab } from './components/WordCountTab';
export { WordCountOptimizer } from './components/WordCountOptimizer';
export { WordCountDisplay, calculateReportWordCount, calculateSectionWordCounts } from './components/WordCountDisplay';
export { WordLimitConfiguration } from './components/WordLimitConfiguration';

// Export RFP Structure Viewer component
export { RFPStructureViewer } from './components/RFPStructureViewer';
